import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';

/**
 * Small "(i)" info button + popover, mirroring proto's `.page-info-btn` /
 * `.page-info-popover` pattern reused per-section as `data-gv-info` (see
 * `wireGVSectionInfo()` in proto.html) — a title, an "About this section"
 * eyebrow, and rich body copy anchored just below the button that opened it.
 */

const InfoBtn = styled.button<{ $open: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: ${({ $open }) =>
    $open ? 'var(--terracotta-soft, #fbe6dc)' : 'transparent'};
  color: ${({ $open }) =>
    $open ? 'var(--terracotta, #c84b1f)' : 'var(--ink-faint, #a2aab4)'};
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.15s;
  flex-shrink: 0;
  padding: 0;
  margin-left: 4px;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    color: var(--terracotta, #c84b1f);
    background: var(--terracotta-soft, #fbe6dc);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.08);
  z-index: 120;
`;

const Popover = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: 440px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow:
    0 12px 32px -8px rgba(43, 61, 84, 0.2),
    0 4px 12px -4px rgba(43, 61, 84, 0.1);
  z-index: 130;
  padding: 20px 22px 22px;
  font-family: var(--font-sans, sans-serif);
  font-size: 13.5px;
  font-weight: 400;
  letter-spacing: normal;
  line-height: 1.55;
  text-align: left;
  text-transform: none;
`;

const Arrow = styled.div`
  position: absolute;
  top: -7px;
  left: 18px;
  width: 14px;
  height: 14px;
  background: var(--white);
  border-top: 1px solid var(--line);
  border-left: 1px solid var(--line);
  transform: rotate(45deg);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Eyebrow = styled.div`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--terracotta, #c84b1f);
  font-weight: 600;
`;

const CloseBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-faint, #a2aab4);
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    border-color: var(--line);
    color: var(--ink);
    background: var(--paper);
  }
`;

const Title = styled.h2`
  font-family: var(--font-serif, serif);
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.015em;
  text-transform: none;
  color: var(--ink);
  line-height: 1.2;
  margin: 0 0 12px;
`;

const Body = styled.div`
  font-size: 13.5px;
  font-family: var(--font-sans, sans-serif);
  font-weight: 400;
  letter-spacing: normal;
  color: var(--ink-muted);
  line-height: 1.55;
  text-transform: none;

  p {
    margin: 0 0 10px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  strong {
    color: var(--ink);
    font-weight: 600;
  }
  em {
    font-style: italic;
    color: var(--terracotta-dark, #9a3a15);
  }
`;

interface SectionInfoProps {
  title: string;
  body: React.ReactNode;
  label?: string;
}

export const SectionInfo: React.FC<SectionInfoProps> = ({
  title,
  body,
  label
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      if (!btnRef.current || !popoverRef.current) return;

      const anchor = btnRef.current.getBoundingClientRect();
      const popover = popoverRef.current.getBoundingClientRect();
      const edge = 16;
      const gap = 10;
      const maxLeft = Math.max(edge, window.innerWidth - popover.width - edge);
      const left = Math.min(Math.max(edge, anchor.left - 12), maxLeft);
      const belowTop = anchor.bottom + gap;
      const aboveTop = anchor.top - popover.height - gap;
      const top =
        belowTop + popover.height <= window.innerHeight - edge
          ? belowTop
          : Math.max(
              edge,
              Math.min(aboveTop, window.innerHeight - popover.height - edge)
            );

      setPos({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    closeRef.current?.focus();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      btnRef.current?.focus();
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const width = Math.min(440, window.innerWidth - 32);
      const left = Math.min(
        Math.max(16, rect.left - 12),
        window.innerWidth - width - 16
      );
      setPos({ top: rect.bottom + 10, left });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <InfoBtn
        ref={btnRef}
        type="button"
        $open={open}
        aria-label={
          label ?? I18n.t('globalView.sectionInfo.openLabel', { title })
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </InfoBtn>
      {open && (
        <>
          <Overlay onClick={() => setOpen(false)} />
          <Popover
            ref={popoverRef}
            $top={pos.top}
            $left={pos.left}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <Arrow />
            <Head>
              <Eyebrow>{I18n.t('globalView.sectionInfo.eyebrow')}</Eyebrow>
              <CloseBtn
                ref={closeRef}
                type="button"
                aria-label={I18n.t('globalView.sectionInfo.close')}
                onClick={() => setOpen(false)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </Head>
            <Title id={titleId}>{title}</Title>
            <Body>{body}</Body>
          </Popover>
        </>
      )}
    </>
  );
};
